import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Loader2 } from 'lucide-react';

import CtaLink from '@/components/CtaLink';
import { useLanguage } from '@/components/LanguageContext';
import httpClient from '@/api/httpClient';
import { pickLandingV3Content } from '@/pages/landingV3.content';

/**
 * ContactoForm — el formulario de captación de la landing.
 *
 * Escribe un lead en `admin_leads` —el mismo sitio que alimenta Admin → Leads—
 * y dispara un aviso por correo. Lo hace el backend
 * (`POST /api/v1/public/contacto/`); aquí solo se recoge y se valida.
 *
 * TRES COSAS QUE EL DISEÑO PEDÍA CAMBIAR, y por qué:
 *
 * 1. **Fuera el fondo negro.** El bloque venía en `#111111` con la tarjeta
 *    blanca encima. Un formulario sobre negro en mitad de una página clara pesa
 *    como un anuncio: se lee antes el bloque que lo que pone dentro. Ahora va
 *    sobre `#fbfbfa`, que es la superficie que ya usa el resto de la V3.
 * 2. **El `textarea` es grande.** En el diseño eran tres líneas apretadas bajo
 *    una rejilla de dos columnas, y el mensaje es lo que de verdad cualifica el
 *    lead: si la caja invita a escribir una línea, se escribe una línea.
 * 3. **Checkbox de política, obligatorio.** Sin él no hay base legal para
 *    guardar los datos de un desconocido. No va premarcado —un consentimiento
 *    premarcado no es consentimiento— y el backend lo vuelve a exigir, porque
 *    una validación que solo vive en el navegador no valida nada.
 *
 * El envío va por `httpClient`, no por `sendBeacon`: esto no es telemetría de
 * un elemento que navega, es una petición normal cuya respuesta hay que mirar.
 */
const TRAMOS = ['5-15', '15-30', '+30'];

const RE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const VACIO = {
  nombre: '',
  apellidos: '',
  telefono: '',
  email: '',
  tramo: '',
  mensaje: '',
  acepta_politica: false,
  // Honeypot. Lo pinta oculto y una persona no puede rellenarlo; un bot que
  // envía el formulario entero, sí. El backend rechaza la petición si llega
  // con algo, antes de escribir nada.
  apellido2: '',
};

export default function ContactoForm({ ctaId }) {
  const { language } = useLanguage();
  const c = pickLandingV3Content(language).contacto;

  const [campos, setCampos] = useState(VACIO);
  const [errores, setErrores] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [errorGeneral, setErrorGeneral] = useState(null);

  const set = (clave) => (valor) => {
    setCampos((f) => ({ ...f, [clave]: valor }));
    // El error de un campo se borra en cuanto se toca. Dejarlo hasta el
    // siguiente envío hace que la página parezca rota mientras se corrige.
    setErrores((e) => (e[clave] ? { ...e, [clave]: undefined } : e));
  };

  function validar() {
    const e = {};
    if (!campos.nombre.trim()) e.nombre = c.errNombre;
    if (!RE_EMAIL.test(campos.email.trim())) e.email = c.errEmail;
    if (!campos.acepta_politica) e.acepta_politica = c.errPolitica;
    return e;
  }

  async function enviar(evento) {
    evento.preventDefault();
    setErrorGeneral(null);

    const e = validar();
    setErrores(e);
    if (Object.keys(e).length > 0) {
      // Al primer campo con error, para no dejar a nadie buscando cuál falla
      // en un formulario de siete campos.
      document.querySelector(`[data-campo="${Object.keys(e)[0]}"]`)?.focus();
      return;
    }

    setEnviando(true);
    try {
      await httpClient.post('/public/contacto/', {
        nombre: campos.nombre.trim(),
        apellidos: campos.apellidos.trim(),
        email: campos.email.trim(),
        telefono: campos.telefono.trim(),
        tramo: campos.tramo,
        mensaje: campos.mensaje.trim(),
        acepta_politica: true,
        apellido2: campos.apellido2,
      });
      setEnviado(true);
    } catch (err) {
      // El 429 se dice distinto: «no hemos podido enviarlo, inténtalo otra vez»
      // sería mentira y además invita justo a lo que el throttle quiere evitar.
      setErrorGeneral(err?.response?.status === 429 ? c.errThrottle : c.errEnvio);
    } finally {
      setEnviando(false);
    }
  }

  if (enviado) {
    return (
      <div className="bg-white rounded-[20px] p-8 text-center v3-shadow" role="status">
        <div className="w-14 h-14 rounded-full bg-v3-greensoft flex items-center justify-center mx-auto">
          <Check className="w-7 h-7 text-v3-green" strokeWidth={2.5} aria-hidden="true" />
        </div>
        <div className="text-[22px] font-bold tracking-[-.02em] mt-4 text-v3-ink">
          {c.okTitulo}
        </div>
        <p className="text-[15px] leading-[1.6] text-v3-body mt-2 max-w-[42ch] mx-auto">
          {c.okCuerpo}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={enviar} noValidate className="bg-white rounded-[20px] p-6 sm:p-7 v3-shadow">
      <div className="grid gap-3 sm:grid-cols-2">
        <Campo
          clave="nombre"
          etiqueta={c.nombre}
          valor={campos.nombre}
          onChange={set('nombre')}
          error={errores.nombre}
          requerido
          autoComplete="given-name"
        />
        <Campo
          clave="apellidos"
          etiqueta={c.apellidos}
          valor={campos.apellidos}
          onChange={set('apellidos')}
          autoComplete="family-name"
        />
        <Campo
          clave="telefono"
          etiqueta={c.telefono}
          valor={campos.telefono}
          onChange={set('telefono')}
          tipo="tel"
          autoComplete="tel"
        />
        <Campo
          clave="email"
          etiqueta={c.email}
          valor={campos.email}
          onChange={set('email')}
          error={errores.email}
          requerido
          tipo="email"
          autoComplete="email"
        />
      </div>

      <fieldset className="mt-4">
        <legend className="text-[11.5px] font-bold text-v3-faint">{c.sedes}</legend>
        <div className="flex gap-2 mt-2">
          {TRAMOS.map((t) => {
            const activo = campos.tramo === t;
            return (
              <button
                key={t}
                type="button"
                aria-pressed={activo}
                onClick={() => set('tramo')(activo ? '' : t)}
                className={`flex-1 border rounded-full text-sm font-semibold py-2.5 px-1.5 transition-colors ${
                  activo
                    ? 'bg-v3-gold border-v3-gold text-v3-ink'
                    : 'bg-white border-v3-line text-v3-body hover:border-v3-faint'
                }`}
              >
                {t}
              </button>
            );
          })}
        </div>
      </fieldset>

      {/* El mensaje, a ancho completo y con sitio de verdad para escribir. */}
      <label className="block mt-4">
        <span className="block text-[11.5px] font-bold text-v3-faint">{c.mensaje}</span>
        <textarea
          data-campo="mensaje"
          rows={6}
          value={campos.mensaje}
          onChange={(e) => set('mensaje')(e.target.value)}
          placeholder={c.mensajePista}
          className="w-full min-h-[140px] border border-v3-line rounded-xl px-3.5 py-3 mt-1.5 text-[14.5px] leading-[1.6] text-v3-ink placeholder:text-v3-faint outline-none resize-y bg-white focus:border-v3-gold transition-colors"
        />
      </label>

      {/* Honeypot. `aria-hidden` + `tabIndex={-1}` para que un lector de
          pantalla y el tabulador lo salten: un humano no debe poder llegar. */}
      <div className="absolute opacity-0 pointer-events-none h-0 overflow-hidden" aria-hidden="true">
        <label>
          Apellido 2
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={campos.apellido2}
            onChange={(e) => set('apellido2')(e.target.value)}
          />
        </label>
      </div>

      <label className="flex items-start gap-3 mt-5 cursor-pointer">
        <input
          type="checkbox"
          data-campo="acepta_politica"
          checked={campos.acepta_politica}
          onChange={(e) => set('acepta_politica')(e.target.checked)}
          aria-invalid={errores.acepta_politica ? 'true' : undefined}
          className="mt-0.5 w-[18px] h-[18px] shrink-0 accent-[#feae2c] cursor-pointer"
        />
        <span className="text-[13.5px] leading-[1.5] text-v3-body">
          {c.politicaA}
          <Link
            to="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            // El enlace vive dentro del `<label>`: sin esto, pulsarlo marcaría
            // la casilla en vez de abrir la política.
            onClick={(e) => e.stopPropagation()}
            className="text-v3-golddeep underline underline-offset-2 hover:text-v3-ink"
          >
            {c.politicaEnlace}
          </Link>
          {c.politicaB}
        </span>
      </label>
      {errores.acepta_politica ? <Error>{errores.acepta_politica}</Error> : null}

      <CtaLink
        as="button"
        cta={ctaId}
        type="submit"
        disabled={enviando}
        className="w-full bg-v3-gold hover:bg-v3-golddeep disabled:opacity-60 disabled:cursor-not-allowed text-v3-ink text-base font-bold rounded-full py-4 mt-5 transition-colors inline-flex items-center justify-center gap-2"
      >
        {enviando ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : null}
        {enviando ? c.enviando : c.enviar}
      </CtaLink>

      {errorGeneral ? (
        <p className="text-[13.5px] text-v3-red text-center mt-3" role="alert">
          {errorGeneral}
        </p>
      ) : null}

      <p className="text-[12.5px] text-v3-muted text-center mt-3">{c.pie}</p>
    </form>
  );
}

function Campo({ clave, etiqueta, valor, onChange, error, requerido, tipo = 'text', autoComplete }) {
  const idError = error ? `${clave}-error` : undefined;
  return (
    <label className="block">
      <span className="block text-[11.5px] font-bold text-v3-faint">
        {etiqueta}
        {requerido ? <span aria-hidden="true"> *</span> : null}
      </span>
      <input
        data-campo={clave}
        type={tipo}
        value={valor}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        aria-required={requerido || undefined}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={idError}
        className={`w-full border rounded-xl px-3.5 py-2.5 mt-1.5 text-[14.5px] text-v3-ink outline-none bg-white transition-colors ${
          error ? 'border-v3-red' : 'border-v3-line focus:border-v3-gold'
        }`}
      />
      {error ? <Error id={idError}>{error}</Error> : null}
    </label>
  );
}

function Error({ id, children }) {
  return (
    <span id={id} className="block text-[12.5px] text-v3-red mt-1">
      {children}
    </span>
  );
}
